// Global Types
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  RegattaMessageType,
} from 'common.data';
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getFileUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
  regattaMessageTypeLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
  publicBorder,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaMessageFields from '../../../fields/RegattaMessageFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaMessage'>>;
  loading: boolean;
};

class RegattaMessagesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaMessage') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaMessage({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaMessage'> = {
      Content: '',
      File: null,
      Name: '',
      Public: true,
      RegattaId,
      Type: RegattaMessageType.organizingAuthority,
    };

    return (
      <SectionHeader
        bottomBorder
        description={'A list of regatta messages that are published on regatta page and sent to skippers'}
        iconClass={'icon-envelope'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaMessageFields item={newItem} />
            )}
            id={'create-regatta-message-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaMessage'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaMessage(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta message')}
          >
            <Button
              color={'info'}
              id={'create-regatta-message-button'}
              size={'sm'}
              title={t('Add new regatta message')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta messages')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaMessage'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={dataItem.File ? getFileUrl({
            cdnUrl: config.CDN_URL,
            file: dataItem.File,
          }) : getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaMessage,
            docType: docType.pdf,
            id,
            language: i18next.language as $Language,
          })}
          id={`regatta-message-file-${id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
        </Button>
        <FormDialog
          fields={(
            <RegattaMessageFields item={dataItem} />
          )}
          id={`update-regatta-message-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaMessage'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaMessage(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta message')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-message-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-message-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaMessage(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta message')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-message-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th />
            <th>{t('Name')}</th>
            <th>{t('Type')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaMessage'>, index: number) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{`#${index + 1}`}</td>
              <td>{dataItem.Name}</td>
              <td>{t(regattaMessageTypeLabel[dataItem.Type])}</td>
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaMessagesSectionView;
